import React, { useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface Props {
  values: { value: string; count: number }[];
  searchText: string;
  filterValueLabel?: (value: string) => string;
  height: number;
  isSelected: (value: string) => boolean;
  isExclusionMode: boolean;
  onToggle: (value: string) => void;
}

const ROW_HEIGHT = 36;

/** Only mount the visible options. Search and bulk actions still use all facets.
 * The parent keys this component by column/search so a new search starts at top. */
export function TanTableFacetList({
  values, searchText, filterValueLabel, height, isSelected, isExclusionMode, onToggle,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const options = useMemo(() => {
    const search = searchText.toLowerCase();
    return values.map(item => ({ ...item, label: filterValueLabel?.(item.value) ?? item.value }))
      .filter(item => !search || item.label.toLowerCase().includes(search));
  }, [values, searchText, filterValueLabel]);
  const virtual = options.length > 100;
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
    enabled: virtual,
  });
  const rows = virtual
    ? virtualizer.getVirtualItems().map(item => ({ index: item.index, start: item.start }))
    : options.map((_, index) => ({ index, start: index * ROW_HEIGHT }));

  return (
    <div ref={scrollRef} aria-label="Filter values" className="overflow-y-auto"
      style={{ height: Math.min(height, Math.max(1, options.length) * ROW_HEIGHT) }}>
      {options.length === 0 ? (
        <div className="text-center py-2 text-xs text-muted">No matching values</div>
      ) : (
        <div style={{ height: options.length * ROW_HEIGHT, position: 'relative' }}>
          {rows.map(({ index, start }) => {
            const item = options[index];
            const checked = isSelected(item.value);
            const excluded = isExclusionMode && checked;
            return (
              <label key={item.value}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${excluded ? 'opacity-60' : 'hover:bg-theme-bg-tertiary'}`}
                style={{ position: 'absolute', top: start, width: '100%', height: ROW_HEIGHT }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(item.value)}
                  className={`rounded border-theme-border-secondary flex-shrink-0 ${isExclusionMode ? 'text-orange-500 focus:ring-orange-500' : 'text-blue-600 focus:ring-blue-500'}`} />
                <span className={`text-sm truncate ${excluded ? 'line-through text-gray-400 dark:text-gray-500' : 'text-label'}`}>
                  {item.label}
                </span>
                <span className="text-xs text-muted ml-auto flex-shrink-0">({item.count})</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
